/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { graphql } from 'gatsby';
import { PageLayout, EnquireFormModal, TrainerSection, FAQ } from 'components';
import {
  TrustBadges,
  StatsBar,
  HowItWorks,
  GuaranteeSection,
  ClientStories,
  CredentialsStripe,
  SuburbLinks,
} from 'components/CRO';
import HeroImage from 'assets/images/cro/hero.jpg';
import { buildBreadcrumbSchema } from 'utils/schema/breadcrumb';
import { buildFaqSchema } from 'utils/schema/faq';
import { buildOrganizationSchema } from 'utils/schema/organization';
import { buildServiceSchema } from 'utils/schema/service';
import { MELBOURNE_PRIORITY_SUBURBS } from '../data/melbournePrioritySuburbs';

const SITE_URL = 'https://www.getgoingpt.com.au';
const PAGE_PATH = '/mobile-personal-training/';
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;
const DEAL_TITLE = 'Mobile PT Melbourne';

const WHY_MOBILE_PT = [
  {
    iconPath: 'M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10',
    title: 'We Come to You',
    body: 'No commute, no gym membership, no waiting for equipment. Your trainer shows up at your door with everything needed for a full session.',
  },
  {
    iconPath:
      'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    title: 'No Gym, No Judgement',
    body: "Skip crowded gyms and intimidating class schedules. Train in a space where you're comfortable, on a timeline that actually works for your life.",
  },
  {
    iconPath: 'M12 12a5 5 0 100-10 5 5 0 000 10zM4 22v-1a7 7 0 0116 0v1',
    title: 'Matched to Your Goals',
    body: 'We hand-pick your coach from our Melbourne team based on your goals, personality, and schedule. Not the first name off a roster.',
  },
];

const WHERE_YOU_TRAIN = [
  {
    title: 'At Home',
    body: 'Living room, backyard, garage, apartment. Your trainer brings the equipment and works with whatever space you have.',
  },
  {
    title: 'In a Park',
    body: "Any of Melbourne's parks, ovals, or beaches. Fresh air, variety, no four-walls monotony.",
  },
  {
    title: 'At Your Gym',
    body: "Already a member somewhere? We'll meet you there and build the session around what's available.",
  },
];

const WHAT_YOU_GET_MAIN = [
  'Weekly 1-on-1 Sessions with Your Trainer at Your Chosen Location',
  'All Equipment Brought to You (or Use What You Already Have)',
  'Custom Training Plan Tailored to Your Goals',
  'Goal-Based Meal Plans & Food Diary Tracking',
  'Unlimited Support from Your Coach via Chat',
  'Progress Check-Ins & Program Adjustments',
  "Access to 100's of Healthy Recipes",
  'Access to 1,000+ Exercise Tutorial Videos',
  'Free Fortnightly Webinars on Training, Nutrition & Mindset',
  'Our Training & Nutrition App',
];

const WHAT_YOU_GET_BONUS = [
  '30 Dietitian-Written Guides on Eating for Your Goals',
  '7 Sports Science Guides on Training for Your Goals',
  'Access to 100 On-Demand Workouts',
  'Access to Monthly Fitness Challenges with Prizes',
];

const PERSONAS = [
  {
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    title: 'Busy Professionals',
    body: "Work long hours, short on time, can't commute to a gym after work. We fit sessions in before work, at lunch, or once the kids are asleep.",
  },
  {
    iconPath: 'M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10',
    title: 'Parents at Home',
    body: 'Young kids, no gym time, no childcare option. Your trainer comes to you - train while the baby naps or the older kids are at school.',
  },
  {
    iconPath: 'M12 22s-8-4.5-8-11V5l8-3 8 3v6c0 6.5-8 11-8 11z',
    title: 'People Intimidated by Gyms',
    body: 'Hate the crowds, the mirrors, the meathead culture. Train in your own space with someone in your corner.',
  },
  {
    iconPath:
      'M12 2l2.39 4.84L20 7.77l-4 3.9L16.94 18 12 15.4 7.06 18 8 11.67l-4-3.9 5.61-.93L12 2z',
    title: 'NDIS Participants',
    body: 'Accredited trainers registered for NDIS support. Train in the environment that works for your plan.',
  },
  {
    iconPath:
      'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M12 12a5 5 0 100-10 5 5 0 000 10zm7-7a3 3 0 11-6 0 3 3 0 016 0zM5 5a3 3 0 11-6 0 3 3 0 016 0z',
    title: 'Corporate Teams',
    body: 'On-site team training for workplaces. Boost culture, health, and retention with a weekly session at the office.',
  },
  {
    iconPath:
      'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
    title: 'Rehab & Injury Return',
    body: 'Coming back from a niggle or surgery. Trainers who understand medical context and pace your return the right way.',
  },
];

const WHY_IT_WORKS = [
  {
    iconPath: 'M12 12a5 5 0 100-10 5 5 0 000 10zM4 22v-1a7 7 0 0116 0v1',
    title: 'Personalised Attention for Real Results',
    body: 'Not the crowded-gym experience. Your coach focuses entirely on you, designs around your body and goals, and adapts the program as you progress.',
  },
  {
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    title: 'Convenient & Time-Saving',
    body: 'No commute, no class schedule. Your session fits your calendar, not the other way around. Before work, at lunch, after bedtime - whatever works.',
  },
  {
    iconPath: 'M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10',
    title: 'Flexibility & Adaptability',
    body: 'Indoor, outdoor, strength, cardio, boxing - your trainer works with what you prefer and what space and equipment are available.',
  },
  {
    iconPath:
      'M12 2l2.39 4.84L20 7.77l-4 3.9L16.94 18 12 15.4 7.06 18 8 11.67l-4-3.9 5.61-.93L12 2z',
    title: 'Expertise & Guidance',
    body: 'Certified professionals who look at the whole picture: fitness, nutrition, mindset. Holistic coaching, not transactional gym bookings.',
  },
  {
    iconPath: 'M12 22s-8-4.5-8-11V5l8-3 8 3v6c0 6.5-8 11-8 11z',
    title: 'Safety First',
    body: 'Form corrected in real time. Programming accounts for injuries, health conditions, and current ability. Build strength without building injuries.',
  },
];

const FIRST_SESSION = [
  {
    title: 'Goal Assessment',
    body: 'A real chat about your goals, lifestyle, limitations, and what "feeling good" looks like to you.',
  },
  {
    title: 'Fitness Evaluation',
    body: 'Simple movement assessments to establish a baseline. No judgement, just data.',
  },
  {
    title: 'Personalised Plan',
    body: "Your trainer designs a program tailored to your goals, schedule, and the space you're training in.",
  },
  {
    title: 'First Session',
    body: 'A full workout with demos, form cues, and real-time feedback. By the end, you know what training with a coach feels like.',
  },
];

const FAQS = [
  {
    question: 'What does a mobile personal trainer actually bring to my home?',
    answer:
      "Everything needed for a full session - dumbbells, resistance bands, mats, agility gear, boxing pads if that's your thing. You just need enough floor space (roughly 2m × 2m works for most sessions). If you already have equipment at home, your trainer uses what you've got.",
  },
  {
    question: 'Do I need any equipment to get started?',
    answer:
      "No. Your trainer brings the essentials to every session. Over time, if you want to add a few bits (a pair of dumbbells, a resistance band) your coach will recommend what's actually useful - not a whole home gym.",
  },
  {
    question: 'How much does mobile personal training in Melbourne cost?',
    answer:
      'Our in-home PT packages run from $99 to $280 per week, depending on how many sessions you train and the length of your package. Your coach will walk you through the options on your free call.',
  },
  {
    question: 'What areas of Melbourne do your trainers cover?',
    answer:
      "We cover all of metro Melbourne - CBD, inner east, inner west, bayside, inner north, and most middle-ring suburbs. Book a free call and we'll confirm coverage in your exact postcode and match you with a coach based nearby.",
  },
  {
    question: 'Can I train outdoors or at a park instead?',
    answer:
      "Yes - that's one of the best things about mobile PT. Your trainer can run sessions at any Melbourne park, oval, or beach. You can switch between home, park, and gym week to week. Your coach adapts.",
  },
  {
    question: 'What if I already have a gym membership?',
    answer:
      "Great - we'll meet you there. Your trainer uses the equipment available at your gym and builds sessions around what's there. No need to double up on memberships or cancel anything.",
  },
  {
    question: "What if my matched trainer isn't the right fit?",
    answer:
      "We offer the Perfect Fit Guarantee. If after your first session your coach isn't the right fit, we'll match you with a different trainer for free. No questions asked.",
  },
  {
    question: 'Do you take NDIS clients?',
    answer:
      "Yes. Our accredited trainers are registered for NDIS support. Training happens in the environment that works for your plan - home, park, or wherever suits. Let us know on your free call and we'll match you with a trainer experienced with NDIS participants.",
  },
];

const Icon: React.FC<{ path: string }> = ({ path }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={path} />
  </svg>
);

type TrainerEdge = {
  node: {
    slug?: string;
    [key: string]: any;
  };
};

type MobilePTPageData = {
  Trainers?: {
    edges?: TrainerEdge[];
  };
};

const MobilePersonalTraining = ({ data }: { data: MobilePTPageData }) => {
  const [isEnquireModalOpen, setIsEnquireModalOpen] = useState(false);

  const openEnquireModal = () => setIsEnquireModalOpen(true);

  const allTrainerEdges = data?.Trainers?.edges ?? [];

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/` },
    { name: 'Mobile Personal Training', url: PAGE_URL },
  ]);

  const serviceSchema = buildServiceSchema({
    title: 'Mobile Personal Training Melbourne',
    serviceType: 'Personal Training',
    areaServed: { '@type': 'City', name: 'Melbourne' },
    description:
      'Mobile personal trainer Melbourne - certified coaches who come to your home, park, or own gym. 1-on-1 sessions, custom programs, 2,000+ 5-star reviews.',
    offers: {
      priceCurrency: 'AUD',
      lowPrice: '99',
      highPrice: '280',
      unitText: 'week',
    },
  });

  const faqSchema = buildFaqSchema(
    FAQS.map(f => ({ question: f.question, answer: f.answer }))
  );
  const organizationSchema = buildOrganizationSchema();

  // NOTE: LocalBusiness schema is intentionally NOT emitted on this page.
  // `buildLocalBusinessSchema` templates its description off a suburb-name
  // `title` ("best PT in {title}, Melbourne") - for a metro-wide page this
  // renders as "Melbourne, Melbourne". Service + Organization schemas cover
  // business info adequately. Same decision was made on the female PT page.

  return (
    <PageLayout
      title="Mobile Personal Training Melbourne"
      metaTitle="Mobile Personal Training Melbourne | Get Going Personal Training"
      description="Mobile personal trainer Melbourne - certified coaches who come to your home, park, or own gym. 1-on-1 sessions, custom programs, 2,000+ 5-star reviews."
      type="Service"
      delaySec={0.3}
      hideBanner
      dealTitle={DEAL_TITLE}
      schema={[organizationSchema, breadcrumbSchema, serviceSchema, faqSchema]}
    >
      {/* 1. Hero */}
      <section className="cro-location-hero">
        <img
          src={HeroImage}
          alt="Mobile personal trainer coaching a client at home in Melbourne"
          className="cro-online-hero-img"
          loading="eager"
        />
        <div className="cro-location-hero__overlay" aria-hidden="true" />
        <div className="cro-location-hero__content">
          <span className="cro-location-hero__badge">
            <span className="cro-location-hero__badge-stars">★★★★★</span> 2,000+
            Five-Star Reviews across Melbourne
          </span>
          <h1 className="cro-location-hero__heading">
            Mobile Personal Training in Melbourne
          </h1>
          <p className="cro-location-hero__accent">
            Your trainer, your schedule, your space.
          </p>
          <p className="cro-location-hero__sub">
            Your certified coach comes to your home, your park, or your own gym
            - anywhere across Melbourne. Private, one-on-one, built around your
            life.
          </p>
          <div className="cro-online-cta">
            <button
              type="button"
              className="cro-cta-button"
              onClick={openEnquireModal}
            >
              Find My Mobile Trainer →
            </button>
          </div>
          <TrustBadges variant="compact" />
        </div>
        <div className="cro-location-hero__stats">
          <div className="cro-location-hero__stats-inner">
            <div className="cro-location-hero__stat">
              <strong>10+</strong>
              <span>Years Experience</span>
            </div>
            <div className="cro-location-hero__stats-divider" />
            <div className="cro-location-hero__stat">
              <strong>2,000+</strong>
              <span>5-Star Reviews</span>
            </div>
            <div className="cro-location-hero__stats-divider" />
            <div className="cro-location-hero__stat">
              <strong className="cro-location-hero__stat-stars">★★★★★</strong>
              <span>4.9 Average Rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Credentials strip */}
      <CredentialsStripe />

      {/* 3. Why Mobile PT - 3-tile strip */}
      <section className="cro-mobile-why">
        <div className="container">
          <h2 className="cro-mobile-why__heading">
            Why Train with a <span>Mobile Personal Trainer in Melbourne</span>
          </h2>
          <div className="cro-mobile-why__grid">
            {WHY_MOBILE_PT.map(tile => (
              <div key={tile.title} className="cro-mobile-why__tile">
                <div className="cro-mobile-why__icon">
                  <Icon path={tile.iconPath} />
                </div>
                <h3 className="cro-mobile-why__tile-title">{tile.title}</h3>
                <p className="cro-mobile-why__tile-body">{tile.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Where You Train - 3-card (NEW unique section) */}
      <section className="cro-mobile-where">
        <div className="container cro-mobile-where__inner">
          <span className="cro-mobile-where__pill">Train Anywhere</span>
          <h2 className="cro-mobile-where__heading">
            Where You <span>Train</span> Is Up to You
          </h2>
          <div className="cro-mobile-where__grid">
            {WHERE_YOU_TRAIN.map(card => (
              <article key={card.title} className="cro-mobile-where__card">
                <div className="cro-mobile-where__card-icon" aria-hidden="true">
                  {card.title === 'At Home' && (
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
                    </svg>
                  )}
                  {card.title === 'In a Park' && (
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2l3 6 6 1-4.5 4.4L18 20l-6-3-6 3 1.5-6.6L3 9l6-1z" />
                    </svg>
                  )}
                  {card.title === 'At Your Gym' && (
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 6h2v12H6zm10 0h2v12h-2zM2 9h4v6H2zm16 0h4v6h-4zM8 11h8v2H8z" />
                    </svg>
                  )}
                </div>
                <h3 className="cro-mobile-where__card-title">{card.title}</h3>
                <p className="cro-mobile-where__card-body">{card.body}</p>
              </article>
            ))}
          </div>
          <p className="cro-mobile-where__closer">
            You can switch between all three week to week. Your trainer adapts.
          </p>
        </div>
      </section>

      {/* 5. Everything You Get - split card */}
      <section className="cro-mobile-whatyouget">
        <div className="cro-mobile-whatyouget__inner">
          <span className="cro-mobile-whatyouget__pill">WHAT YOU GET</span>
          <h2 className="cro-mobile-whatyouget__heading">
            Not Just 'PT' - A Personalised Program Built{' '}
            <span>Around Your Life</span>
          </h2>
          <p className="cro-mobile-whatyouget__intro">
            This isn't a one-size-fits-all program. Your coach assesses where
            you're at, designs for your goals and lifestyle, and adapts it as
            you progress.
          </p>
          <div className="cro-mobile-whatyouget__card">
            <div className="cro-mobile-whatyouget__col cro-mobile-whatyouget__col--included">
              <div className="cro-mobile-whatyouget__col-head">
                <span
                  className="cro-mobile-whatyouget__col-icon"
                  aria-hidden="true"
                >
                  ✓
                </span>
                <span className="cro-mobile-whatyouget__col-label">
                  Included
                </span>
              </div>
              <ul className="cro-mobile-whatyouget__list">
                {WHAT_YOU_GET_MAIN.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="cro-mobile-whatyouget__card-divider" />
            <div className="cro-mobile-whatyouget__col cro-mobile-whatyouget__col--bonus">
              <div className="cro-mobile-whatyouget__col-head">
                <span
                  className="cro-mobile-whatyouget__col-icon"
                  aria-hidden="true"
                >
                  ★
                </span>
                <span className="cro-mobile-whatyouget__col-label">
                  And as a bonus
                </span>
              </div>
              <ul className="cro-mobile-whatyouget__list">
                {WHAT_YOU_GET_BONUS.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 6. How It Works */}
      <HowItWorks />

      {/* 7. Perfect Fit Guarantee */}
      <GuaranteeSection />

      {/* 8. Client Stories */}
      <ClientStories />

      {/* 9. Mid CTA */}
      <section className="cro-location-midcta">
        <div className="cro-location-midcta__inner">
          <span className="cro-location-midcta__pill">Your Move</span>
          <h2 className="cro-location-midcta__heading">
            Ready to Find <span>Your Trainer?</span>
          </h2>
          <p className="cro-location-midcta__sub">
            Book a free 15-minute call. We'll match you with a Melbourne coach
            who fits your goals, schedule, and training style.
          </p>
          <div className="cro-online-cta">
            <button
              type="button"
              className="cro-cta-button"
              onClick={openEnquireModal}
            >
              Find My Mobile Trainer →
            </button>
          </div>
          <TrustBadges variant="compact" />
        </div>
      </section>

      {/* 10. Who It's For - 6 personas 3x2 grid */}
      <section className="cro-mobile-personas">
        <div className="container cro-mobile-personas__inner">
          <span className="cro-mobile-personas__pill">Who It's For</span>
          <h2 className="cro-mobile-personas__heading">
            Built for <span>Busy Melbourne Lives</span>
          </h2>
          <div className="cro-mobile-personas__grid">
            {PERSONAS.map(p => (
              <div key={p.title} className="cro-mobile-personas__card">
                <div
                  className="cro-mobile-personas__card-icon"
                  aria-hidden="true"
                >
                  <Icon path={p.iconPath} />
                </div>
                <h3 className="cro-mobile-personas__card-title">{p.title}</h3>
                <p className="cro-mobile-personas__card-body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Why Mobile PT Works - 5-tile */}
      <section className="cro-mobile-whyitworks">
        <div className="container cro-mobile-whyitworks__inner">
          <h2 className="cro-mobile-whyitworks__heading">
            Why <span>Mobile Personal Training</span> Works
          </h2>
          <p className="cro-mobile-whyitworks__intro">
            A mobile personal trainer in Melbourne is gaining popularity because
            it's flexible, tailored, and removes the barriers people actually
            quit over. Here's why it works.
          </p>
          <div className="cro-mobile-whyitworks__grid">
            {WHY_IT_WORKS.map(tile => (
              <div key={tile.title} className="cro-mobile-whyitworks__tile">
                <div className="cro-mobile-whyitworks__icon">
                  <Icon path={tile.iconPath} />
                </div>
                <h3 className="cro-mobile-whyitworks__tile-title">
                  {tile.title}
                </h3>
                <p className="cro-mobile-whyitworks__tile-body">{tile.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 12. What to Expect - Your First Session 4-step */}
      <section className="cro-mobile-firstsession">
        <div className="container cro-mobile-firstsession__inner">
          <span className="cro-mobile-firstsession__pill">Getting Started</span>
          <h2 className="cro-mobile-firstsession__heading">
            From Your <span>Free Call</span> to Your First Session
          </h2>
          <div className="cro-mobile-firstsession__grid">
            {FIRST_SESSION.map((step, idx) => (
              <div key={step.title} className="cro-mobile-firstsession__step">
                <div className="cro-mobile-firstsession__number">
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <h3 className="cro-mobile-firstsession__step-title">
                  {step.title}
                </h3>
                <p className="cro-mobile-firstsession__step-body">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 13. Build Health, Wellness & Confidence - editorial block */}
      <section className="cro-mobile-wellness">
        <div className="container cro-mobile-wellness__inner">
          <blockquote className="cro-mobile-wellness__quote">
            "Mental and physical health go hand in hand."
          </blockquote>
          <div className="cro-mobile-wellness__body">
            <p>
              The beauty of personal training is its ability to address both at
              the same time. When you get started, you feel the physiological
              benefits as you work toward a healthier body. But the secret is
              you soon see the positive impacts on your mental state too -
              exercise releases endorphins, the body's natural mood regulators,
              reducing stress and anxiety and lifting how you feel day to day.
            </p>
            <p>
              At Get Going we love the community aspect. Working with a trainer,
              sometimes alongside family or friends, creates a supportive
              environment where you share experiences, challenges, and triumphs.
              That social layer is another way to feel better, healthier, and
              happier.
            </p>
            <p className="cro-mobile-wellness__tagline">
              <strong>Make it easy. Make it a routine. Make it a habit.</strong>
            </p>
            <p>
              Your Get Going coach designs training tailored to your individual
              needs and goals. Through strength work, nutrition guidance,
              mobility, and the accountability of showing up weekly, you see
              real improvement in your physical and mental well-being within a
              few short weeks.
            </p>
          </div>
        </div>
      </section>

      {/* 13.5 Mobile PT suburb directory - internal linking */}
      <SuburbLinks
        eyebrow="Mobile PT Across Melbourne"
        heading="Mobile Personal Training in Your Suburb"
        subheading="Tap your suburb to see local coverage, training spots, and reviews from neighbours."
        suburbs={MELBOURNE_PRIORITY_SUBURBS}
        variant="priority"
      />

      {/* 14. FAQ */}
      <div className="container">
        <section className="home-page-faq-section">
          <FAQ
            Faqs={FAQS.map((faq, index) => ({
              id: index + 1,
              title: faq.question,
              answer: faq.answer,
            }))}
          />
        </section>
      </div>

      {/* 15. Final CTA */}
      <section className="cro-location-final-cta">
        <div className="cro-location-final-cta__inner">
          <span className="cro-location-final-cta__pill">Ready to Start?</span>
          <h2 className="cro-location-final-cta__heading">
            Your Trainer. Your Schedule. <span>Your Results.</span>
          </h2>
          <p className="cro-location-final-cta__sub">
            Book your free call today. We'll match you with a Melbourne coach
            within 7 days. Backed by our Perfect Fit Guarantee.
          </p>
          <div className="cro-online-cta">
            <button
              type="button"
              className="cro-cta-button"
              onClick={openEnquireModal}
            >
              Find My Mobile Trainer →
            </button>
          </div>
          <TrustBadges variant="compact" />
          <StatsBar />
        </div>
      </section>

      {/* 16. Meet Your Melbourne Coaches — unfiltered TrainerSection */}
      {allTrainerEdges.length > 0 && (
        <TrainerSection
          personalTrainer={allTrainerEdges}
          sectionHeading={{
            title: 'Meet Your Melbourne Coaches',
            tagline:
              "Every coach on this list is certified, insured, and works across Melbourne. We'll match you with the right one on your free call.",
            seeAllText: 'See all trainers',
          }}
        />
      )}

      <EnquireFormModal
        isEnquireModalOpen={isEnquireModalOpen}
        setisEnquireModalOpen={setIsEnquireModalOpen}
        contactFormModalTitle="Book Your Free Call - Mobile PT Melbourne"
        optionalMessage
        dealTitle={DEAL_TITLE}
        showPostcodeField
      />
    </PageLayout>
  );
};

export default MobilePersonalTraining;

export const query = graphql`
  query MobilePTMelbourneQuery {
    Trainers: allStrapiPersonalTrainers {
      edges {
        node {
          name
          slug
          id
          qualification
          description
          image {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 520, height: 520)
              }
            }
          }
        }
      }
    }
  }
`;
